// The camera (PRD section 14.2): the map's perspective tilted about 50 degrees and turned about 35
// degrees, the interior's nearly top-down one (about 70 degrees), and a "Cenital" top-down view for
// both; pan that keeps the touched ground under the finger, pinch and wheel zoom around the fingers,
// and a gentle glide after a flick. A framing describes each level; the rig is the same for both.
import { PerspectiveCamera, Plane, Raycaster, Vector2, Vector3 } from 'three';
import type { ScreenPoint } from '../input/gestures';

export type CameraMode = 'tilted' | 'top';

export interface Angles {
  /** Turn around the vertical axis, in radians. */
  readonly yaw: number;
  /** Height above the horizon, in radians. */
  readonly pitch: number;
}

/** Angles of each map mode. */
export const MODE_ANGLES: Record<CameraMode, Angles> = {
  tilted: { yaw: 0.62, pitch: 0.9 },
  top: { yaw: 0, pitch: Math.PI / 2 - 0.01 },
};

/** The mockup's iPad camera (scene.js PRESET 'map-ipad') at 1180 x 820. */
export const REFERENCE = { tx: 0.2, ty: -0.3, tz: 0.9, distance: 21, fov: 32, aspect: 1180 / 820 } as const;

/** Where each map mode looks when the player has not panned: the tilted view aims a bit south of the centre. */
const MODE_TARGETS: Record<CameraMode, { readonly x: number; readonly z: number }> = {
  tilted: { x: REFERENCE.tx, z: REFERENCE.tz },
  top: { x: 0, z: 0 },
};

/** Sea left around the island when the top view fits it on screen, in tiles. */
const TOP_MARGIN = 0.6;

/** Map zoom limits as multiples of the distance that fits the island on screen. */
export const ZOOM_LIMITS = { min: 0.3, max: 1.4 } as const;

const MODE_TWEEN_MS = 600;
const SLIDE_MS = 420;
/** Time constant of the glide after a flick, in milliseconds. */
const GLIDE_TAU_MS = 325;
const GLIDE_MIN_SPEED = 0.00005;

/**
 * Distance that shows the same island width as the reference camera: in portrait or on narrow
 * screens the camera backs off so the island still fits across.
 */
export function fitDistance(aspect: number): number {
  if (aspect >= REFERENCE.aspect) return REFERENCE.distance;
  const half = (REFERENCE.fov * Math.PI) / 360;
  const refHalfWidth = Math.atan(Math.tan(half) * REFERENCE.aspect);
  const halfWidth = Math.atan(Math.tan(half) * aspect);
  return (REFERENCE.distance * Math.tan(refHalfWidth)) / Math.tan(halfWidth);
}

/** Distance at which the top-down camera shows the whole island, islets included. */
export function fitTopDistance(aspect: number, bounds: Bounds): number {
  const halfV = (REFERENCE.fov * Math.PI) / 360;
  const halfH = Math.atan(Math.tan(halfV) * aspect);
  const halfDepth = (bounds.maxZ - bounds.minZ) / 2 + TOP_MARGIN;
  const halfWidth = (bounds.maxX - bounds.minX) / 2 + TOP_MARGIN;
  return Math.max(halfDepth / Math.tan(halfV), halfWidth / Math.tan(halfH));
}

export const easeOutCubic = (t: number): number => 1 - (1 - Math.min(1, Math.max(0, t))) ** 3;
export const easeInOutCubic = (t: number): number => {
  const x = Math.min(1, Math.max(0, t));
  return x < 0.5 ? 4 * x * x * x : 1 - (-2 * x + 2) ** 3 / 2;
};

/** Camera position for a target, distance, yaw and pitch. */
export function orbitPosition(target: Vector3, distance: number, yaw: number, pitch: number, out = new Vector3()): Vector3 {
  const h = Math.cos(pitch) * distance;
  return out.set(target.x + Math.sin(yaw) * h, target.y + Math.sin(pitch) * distance, target.z + Math.cos(yaw) * h);
}

export interface Bounds {
  readonly minX: number;
  readonly maxX: number;
  readonly minZ: number;
  readonly maxZ: number;
}

/** A rectangle of the screen, in CSS pixels. */
export interface Rect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

export interface Viewport {
  readonly width: number;
  readonly height: number;
  /** HUD panels over the scene: a framing that cares keeps its content clear of them. */
  readonly obstacles: readonly Rect[];
}

/** How a framing shows its content in a viewport. */
export interface Fit {
  readonly distance: number;
  /** CSS pixels the point looked at sits below the middle of the screen, to clear the HUD. */
  readonly shiftY: number;
}

/** Where a camera is: the point it looks at, how far away, from which angles, and the frame's shift. */
export interface Pose extends Angles {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly distance: number;
  readonly shiftY: number;
}

/** What a level of the game shows and how the camera frames it. */
export interface CameraFraming {
  readonly fov: number;
  readonly angles: Readonly<Record<CameraMode, Angles>>;
  /** Ground point each mode looks at when the player has not panned, and the height of that ground. */
  readonly targets: Readonly<Record<CameraMode, { readonly x: number; readonly z: number }>>;
  readonly targetY: number;
  /** How to frame the content in a mode, in a viewport. */
  fit(mode: CameraMode, view: Viewport): Fit;
  /** The target stays over these bounds, give or take `margin`. */
  readonly bounds: Bounds;
  readonly margin: number;
  /** Zoom limits as multiples of the fit distance. */
  readonly zoom: { readonly min: number; readonly max: number };
}

/** The island map's framing: the mockup's camera, the whole island in the top view. */
export function mapFraming(bounds: Bounds): CameraFraming {
  return {
    fov: REFERENCE.fov,
    angles: MODE_ANGLES,
    targets: MODE_TARGETS,
    targetY: REFERENCE.ty,
    fit: (mode, view) => {
      const aspect = view.width / Math.max(1, view.height);
      return { distance: mode === 'top' ? fitTopDistance(aspect, bounds) : fitDistance(aspect), shiftY: 0 };
    },
    bounds,
    margin: 1,
    zoom: ZOOM_LIMITS,
  };
}

/** Places a camera at a pose, with the viewport's aspect and the frame shifted down by the pose's shift. */
export function placeCamera(camera: PerspectiveCamera, pose: Pose, view: Viewport, scratch = new Vector3()): void {
  const { width, height } = view;
  if (Math.abs(pose.shiftY) > 1e-6) camera.setViewOffset(width, height, 0, -pose.shiftY, width, height);
  else if (camera.view?.enabled) camera.clearViewOffset();
  camera.aspect = width / Math.max(1, height);
  camera.updateProjectionMatrix();
  orbitPosition(scratch.set(pose.x, pose.y, pose.z), pose.distance, pose.yaw, pose.pitch, camera.position);
  camera.lookAt(scratch);
  camera.updateMatrixWorld();
}

interface Tween {
  readonly start: number;
  readonly from: number;
  readonly to: number;
}

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** A pose between two others, for the camera flights of the map-interior transition. */
export function lerpPose(a: Pose, b: Pose, t: number): Pose {
  // The distance moves geometrically, so a zoom feels even from far and near.
  const distance = a.distance * (b.distance / a.distance) ** t;
  return { x: lerp(a.x, b.x, t), y: lerp(a.y, b.y, t), z: lerp(a.z, b.z, t), distance, yaw: lerp(a.yaw, b.yaw, t), pitch: lerp(a.pitch, b.pitch, t), shiftY: lerp(a.shiftY, b.shiftY, t) };
}

export class CameraRig {
  readonly camera: PerspectiveCamera;
  /** Point the camera looks at: the mode's reference target plus the player's pan offset. */
  readonly target: Vector3;
  private modeValue: CameraMode = 'tilted';
  /** 0 is the tilted view, 1 the top view; values in between happen during the switch. */
  private blend = 0;
  /** How far the player panned away from the mode's reference target. */
  private readonly offset = new Vector2();
  /** Distance as a multiple of the fit distance; 1 is the reference framing. */
  private zoomScale = 1;
  private view: Viewport = { width: REFERENCE.aspect, height: 1, obstacles: [] };
  private tween: Tween | undefined;
  /** A slide of the pan offset, to bring something into view. */
  private slide: { readonly start: number; readonly from: Vector2; readonly to: Vector2 } | undefined;
  private readonly velocity = new Vector2();
  private gliding = false;
  private readonly samples: { t: number; x: number; z: number }[] = [];
  private readonly framing: CameraFraming;
  private readonly raycaster = new Raycaster();
  private readonly ground: Plane;
  private readonly ndc = new Vector2();
  private readonly scratch = new Vector3();

  constructor(framing: CameraFraming) {
    this.framing = framing;
    this.target = new Vector3(framing.targets.tilted.x, framing.targetY, framing.targets.tilted.z);
    this.ground = new Plane(new Vector3(0, 1, 0), 0);
    this.camera = new PerspectiveCamera(framing.fov, REFERENCE.aspect, 0.1, 200);
    this.apply();
  }

  get mode(): CameraMode {
    return this.modeValue;
  }

  get viewport(): Viewport {
    return this.view;
  }

  /** How the content is framed right now, before the player's zoom. */
  get fit(): Fit {
    const a = this.framing.fit('tilted', this.view);
    const b = this.framing.fit('top', this.view);
    return { distance: lerp(a.distance, b.distance, this.blend), shiftY: lerp(a.shiftY, b.shiftY, this.blend) };
  }

  get distance(): number {
    return this.fit.distance * this.zoomScale;
  }

  /** Where the camera rests: what it shows when no flight overrides it. */
  get pose(): Pose {
    const { yaw, pitch } = this.angles();
    return { x: this.target.x, y: this.target.y, z: this.target.z, distance: this.distance, yaw, pitch, shiftY: this.fit.shiftY };
  }

  /** The resting pose the camera would have with no pan and no zoom, in a mode. */
  homePose(mode: CameraMode = this.modeValue): Pose {
    const f = this.framing;
    return { x: f.targets[mode].x, y: f.targetY, z: f.targets[mode].z, ...f.fit(mode, this.view), ...f.angles[mode] };
  }

  /** True while a tween, slide or glide still needs frames. */
  get moving(): boolean {
    return this.tween !== undefined || this.slide !== undefined || this.gliding;
  }

  setAspect(aspect: number): void {
    this.setViewport({ width: aspect, height: 1, obstacles: [] });
  }

  setViewport(view: Viewport): void {
    this.view = view;
    this.apply();
  }

  /** Switches between the tilted and the top view with a short animation (or at once if `now` is undefined). */
  setMode(mode: CameraMode, now?: number): void {
    if (mode === this.modeValue) return;
    this.modeValue = mode;
    const to = mode === 'top' ? 1 : 0;
    if (now === undefined) {
      this.blend = to;
      this.tween = undefined;
      this.apply();
    } else {
      this.tween = { start: now, from: this.blend, to };
    }
  }

  /**
   * Slides the camera so that the ground under screen point `from` ends under screen point `to`,
   * with a short ease (to bring a tile out from under a panel).
   */
  slideTo(from: ScreenPoint, to: ScreenPoint, width: number, height: number, now: number): void {
    const a = this.pick(from, width, height);
    const b = this.pick(to, width, height);
    if (!a || !b) return;
    this.stopGlide();
    const start = this.offset.clone();
    this.offset.set(start.x + a.x - b.x, start.y + a.z - b.z);
    this.clampOffset();
    const end = this.offset.clone();
    this.offset.copy(start);
    this.slide = { start: now, from: start, to: end };
  }

  /** Back to the mode's home framing: no pan, no zoom, no glide. */
  reset(): void {
    this.offset.set(0, 0);
    this.zoomScale = 1;
    this.slide = undefined;
    this.stopGlide();
    this.apply();
  }

  /** Puts the camera at a pose for one frame (a flight); the rig's own state is left alone. */
  place(pose: Pose): void {
    placeCamera(this.camera, pose, this.view, this.scratch);
  }

  /** Puts the camera back where the rig rests. */
  restore(): void {
    this.apply();
  }

  /** Point of the ground plane under a screen position, if any. */
  pick(p: ScreenPoint, width: number, height: number): Vector3 | undefined {
    this.ndc.set((p.x / width) * 2 - 1, -(p.y / height) * 2 + 1);
    this.raycaster.setFromCamera(this.ndc, this.camera);
    return this.raycaster.ray.intersectPlane(this.ground, new Vector3()) ?? undefined;
  }

  /** Screen position of a world point, in CSS pixels. */
  project(x: number, y: number, z: number, width: number, height: number): ScreenPoint {
    const v = this.scratch.set(x, y, z).project(this.camera);
    return { x: ((v.x + 1) / 2) * width, y: ((1 - v.y) / 2) * height };
  }

  beginGesture(): void {
    this.halt();
    this.samples.length = 0;
  }

  /** Stops any glide or slide where it is (the view is about to change level). */
  halt(): void {
    this.stopGlide();
    this.slide = undefined;
  }

  pan(from: ScreenPoint, to: ScreenPoint, width: number, height: number, now: number): void {
    const a = this.pick(from, width, height);
    const b = this.pick(to, width, height);
    if (!a || !b) return;
    this.offset.x += a.x - b.x;
    this.offset.y += a.z - b.z;
    this.clampOffset();
    this.apply();
    this.samples.push({ t: now, x: this.offset.x, z: this.offset.y });
    while (this.samples.length > 0 && now - (this.samples[0]?.t ?? now) > 100) this.samples.shift();
  }

  /**
   * Zooms by `factor` (above 1 zooms in) around a screen point, within the limits. Returns the part
   * of the factor the limits held back: 1 when it all applied, below 1 when pinching out past the
   * widest view, above 1 when pinching in past the closest.
   */
  zoom(factor: number, at: ScreenPoint, width: number, height: number): number {
    const before = this.pick(at, width, height);
    const wanted = this.zoomScale / factor;
    const { min, max } = this.framing.zoom;
    this.zoomScale = Math.min(max, Math.max(min, wanted));
    this.apply();
    const after = this.pick(at, width, height);
    if (before && after) {
      this.offset.x += before.x - after.x;
      this.offset.y += before.z - after.z;
      this.clampOffset();
      this.apply();
    }
    return this.zoomScale / wanted;
  }

  /** Ends a gesture and starts a glide with the speed of the last 100 ms. */
  endGesture(now: number): void {
    const first = this.samples[0];
    const last = this.samples[this.samples.length - 1];
    this.samples.length = 0;
    if (!first || !last || last.t - first.t < 16 || now - last.t > 50) return;
    const dt = last.t - first.t;
    this.velocity.set((last.x - first.x) / dt, (last.z - first.z) / dt);
    this.gliding = this.velocity.length() > GLIDE_MIN_SPEED * 4;
  }

  stopGlide(): void {
    this.gliding = false;
    this.velocity.set(0, 0);
  }

  /** Advances the tween and the glide. Returns true while more frames are needed. */
  tick(now: number, deltaMs: number): boolean {
    if (this.tween) {
      const k = easeOutCubic((now - this.tween.start) / MODE_TWEEN_MS);
      this.blend = lerp(this.tween.from, this.tween.to, k);
      if (k >= 1) this.tween = undefined;
    }
    if (this.slide) {
      const k = easeOutCubic((now - this.slide.start) / SLIDE_MS);
      this.offset.lerpVectors(this.slide.from, this.slide.to, k);
      if (k >= 1) this.slide = undefined;
    }
    if (this.gliding) {
      const dt = Math.min(deltaMs, 50);
      this.offset.x += this.velocity.x * dt;
      this.offset.y += this.velocity.y * dt;
      this.velocity.multiplyScalar(Math.exp(-dt / GLIDE_TAU_MS));
      if (this.clampOffset() || this.velocity.length() < GLIDE_MIN_SPEED) this.stopGlide();
    }
    this.apply();
    return this.moving;
  }

  /** Keeps the target over the content (with a margin). Returns true if it had to move it. */
  private clampOffset(): boolean {
    const { bounds, margin } = this.framing;
    const base = this.baseTarget();
    const x = Math.min(bounds.maxX + margin, Math.max(bounds.minX - margin, base.x + this.offset.x)) - base.x;
    const z = Math.min(bounds.maxZ + margin, Math.max(bounds.minZ - margin, base.z + this.offset.y)) - base.z;
    const clamped = Math.abs(x - this.offset.x) > 1e-9 || Math.abs(z - this.offset.y) > 1e-9;
    this.offset.set(x, z);
    return clamped;
  }

  private baseTarget(): { x: number; z: number } {
    const a = this.framing.targets.tilted;
    const b = this.framing.targets.top;
    return { x: lerp(a.x, b.x, this.blend), z: lerp(a.z, b.z, this.blend) };
  }

  private angles(): Angles {
    const a = this.framing.angles.tilted;
    const b = this.framing.angles.top;
    return { yaw: lerp(a.yaw, b.yaw, this.blend), pitch: lerp(a.pitch, b.pitch, this.blend) };
  }

  private apply(): void {
    const base = this.baseTarget();
    this.target.set(base.x + this.offset.x, this.framing.targetY, base.z + this.offset.y);
    placeCamera(this.camera, this.pose, this.view, this.scratch);
  }
}
